import { AnimatedSection } from "./AnimatedSection"
import { FloatingCard } from "./FloatingCard"
import { AmplifySection } from "./AmplifySection"
import { StorySection } from "./StorySection"
import { HowItWorksSection } from "./HowItWorksSection"
import { Button } from "@/components/ui/button"
import { ArrowRight, Target, TrendingUp, Calendar, Lightbulb, Zap, MessageSquare, Play } from "lucide-react"
import { Link } from "wouter"
import agent1 from "@assets/Gemini_Generated_Image_jp7nmijp7nmijp7n (1)_1760809993862.png"
import agent2 from "@assets/Gemini_Generated_Image_vnvxzyvnvxzyvnvx (1)_1760810006394.png"
import agent3 from "@assets/Gemini_Generated_Image_3pwcq03pwcq03pwc_1760810036453.png"
import agent4 from "@assets/Gemini_Generated_Image_akydq1akydq1akyd_1760810088993.png"

export function Hero() {
  const agents = [
    { id: 1, image: agent1, alt: "Lina - Content Strategist" },
    { id: 2, image: agent2, alt: "Analytics Specialist" },
    { id: 3, image: agent3, alt: "Visual Content Creator" },
    { id: 4, image: agent4, alt: "Creative Content Manager" },
  ]

  const handleGetStarted = () => {
    window.location.href = "/login"
  }

  return (
    <section id="meet-lina" className="py-8 lg:py-12">
      <div className="container px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          {/* Headline + Sub-Headline */}
          <AnimatedSection direction="up" className="text-center space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold text-balance font-playfair leading-tight">
              Du hasst Social Media.{" "}
              <span className="text-primary">Dein Team liebt es.</span>
              <br />
              <span className="text-muted-foreground text-3xl lg:text-5xl">
                Warum? Es sind KI-Agenten.
              </span>
            </h1>
            
            <div className="max-w-4xl mx-auto space-y-4">
              <p className="text-lg lg:text-xl text-muted-foreground text-pretty">
                Ein Team von KI-Spezialisten arbeitet rund um die Uhr und produziert{" "}
                <span className="font-semibold text-foreground">60 Posts pro Monat</span> auf 
                LinkedIn, Facebook, Instagram und Google My Business.
              </p>
              <p className="text-lg lg:text-xl text-muted-foreground text-pretty">
                Kosten: <span className="font-bold text-primary">ab 249€/Monat</span> – weniger als ein Tag deiner Zeit wert ist.
              </p>
              <p className="text-base lg:text-lg text-muted-foreground text-pretty">
                Keine Müdigkeit. Keine Schreibblockaden. Keine vergessenen Posts.
                <br />
                <span className="font-semibold text-foreground">Das ist Linas A-Team. Und es gehört dir.</span>
              </p>
            </div>
          </AnimatedSection>

          {/* CTA Button - Above Agent Cards */}
          <AnimatedSection delay={150} direction="up" className="flex justify-center">
            <Button 
              variant="default"
              size="lg"
              asChild
              className="bg-orange-500 text-white border-0 animate-pulse-subtle"
              data-testid="button-cta-top"
            >
              <Link href="/vsl">
                <Play className="mr-2 h-5 w-5 fill-current" />
                Film ab, Lina erklärt
              </Link>
            </Button>
          </AnimatedSection>

          {/* 4 Agent Cards - 1x4 Horizontal Grid */}
          <AnimatedSection delay={200} direction="up">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {agents.map((agent, index) => (
                <AnimatedSection 
                  key={agent.id} 
                  delay={300 + index * 100} 
                  direction="up"
                  className="h-full"
                >
                  {index === 0 ? (
                    /* Lina Card with Flip Animation */
                    (<div className="space-y-2" data-testid="agent-wrapper-0">
                      <h3 className="text-center font-playfair font-bold text-lg text-foreground">
                        LINA - The Strategist
                      </h3>
                      <div 
                        className="flip-card-container group cursor-pointer"
                        style={{ perspective: '1000px' }}
                        data-testid="agent-card-0"
                      >
                        <div className="flip-card-inner relative w-full aspect-[3/4] transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                          {/* Front Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <img
                              src={agent.image}
                              alt={agent.alt}
                              className="w-full h-full object-cover"
                              data-testid="agent-img-0"
                            />
                          </div>
                          
                          {/* Back Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <div className="w-full h-full p-3 flex flex-col justify-between">
                              <div className="space-y-2.5">
                                {/* Persönlichkeit */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Persönlichkeit</h4>
                                  <div className="space-y-1">
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Coffee:</span> Americano am Morgen, grüner Tee am Abend
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Talent:</span> Sieht Trends 48h bevor sie viral gehen
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Quirk:</span> Plant ihren Urlaub wie Content-Kalender
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Hobby:</span> Studiert erfolgreiche Werbekampagnen der 60er-80er
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Fun Fact:</span> Content-Strategie auf Emoji-Basis - funktionierte perfekt
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Kern-Fähigkeiten */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Kern-Fähigkeiten</h4>
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                    <div className="flex items-start gap-1.5">
                                      <Target className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Content Strategy</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Lightbulb className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Editorial Calendar</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <TrendingUp className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Trend Analysis & Monitoring</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Zap className="h-3 w-3 text-orange-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Competitive Intelligence</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Calendar className="h-3 w-3 text-purple-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Campaign Planning</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Signature Message */}
                              <div className="flex items-start gap-2 pt-1.5 border-t border-border/40">
                                <MessageSquare className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                                <p className="text-xs italic text-foreground leading-tight">
                                  <span className="font-semibold">LINA's Signature:</span> "Ich liebe es, wenn eine Content-Strategie aufgeht."
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>)
                  ) : index === 1 ? (
                    /* Alex Card with Flip Animation */
                    (<div className="space-y-2" data-testid="agent-wrapper-1">
                      <h3 className="text-center font-playfair font-bold text-lg text-foreground">
                        ALEX - The Analyst
                      </h3>
                      <div 
                        className="flip-card-container group cursor-pointer"
                        style={{ perspective: '1000px' }}
                        data-testid="agent-card-1"
                      >
                        <div className="flip-card-inner relative w-full aspect-[3/4] transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                          {/* Front Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <img
                              src={agent.image}
                              alt={agent.alt}
                              className="w-full h-full object-cover"
                              data-testid="agent-img-1"
                            />
                          </div>
                          
                          {/* Back Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <div className="w-full h-full p-3 flex flex-col justify-between">
                              <div className="space-y-2.5">
                                {/* Persönlichkeit */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Persönlichkeit</h4>
                                  <div className="space-y-1">
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Coffee:</span> Cold Brew - effizient, datiert nicht
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Talent:</span> Findet Muster, wo andere Chaos sehen
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Quirk:</span> Trackt eigenen Koffein-Konsum in Echtzeit (in Excel)
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Hobby:</span> Spielt Schach gegen KI (und analysiert die Daten)
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Fun Fact:</span> Content-Strategie nur auf Wetter-Prognosen - 40% mehr Engagement
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Kern-Fähigkeiten */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Kern-Fähigkeiten</h4>
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                    <div className="flex items-start gap-1.5">
                                      <TrendingUp className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Performance Analytics</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Target className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">A/B Testing & Optimization</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Calendar className="h-3 w-3 text-purple-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Survey & Data Viz</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Lightbulb className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Voice-to-Insights</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Signature Message */}
                              <div className="flex items-start gap-2 pt-1.5 border-t border-border/40">
                                <MessageSquare className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                                <p className="text-xs italic text-foreground leading-tight">
                                  <span className="font-semibold">ALEX's Signature:</span> "Die Daten fliegen mir zu!"
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>)
                  ) : index === 2 ? (
                    /* Emma Card with Flip Animation */
                    (<div className="space-y-2" data-testid="agent-wrapper-2">
                      <h3 className="text-center font-playfair font-bold text-lg text-foreground">
                        EMMA - The Creator
                      </h3>
                      <div 
                        className="flip-card-container group cursor-pointer"
                        style={{ perspective: '1000px' }}
                        data-testid="agent-card-2"
                      >
                        <div className="flip-card-inner relative w-full aspect-[3/4] transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                          {/* Front Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <img
                              src={agent.image}
                              alt={agent.alt}
                              className="w-full h-full object-cover"
                              data-testid="agent-img-2"
                            />
                          </div>
                          
                          {/* Back Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <div className="w-full h-full p-3 flex flex-col justify-between">
                              <div className="space-y-2.5">
                                {/* Persönlichkeit */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Persönlichkeit</h4>
                                  <div className="space-y-1">
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Coffee:</span> Flat White - perfekte Balance, wie guter Content
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Talent:</span> Sieht in jedem Foto 12 verschiedene Stories
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Quirk:</span> Beurteilt Menschen nach ihrer Instagram-Ästhetik
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Hobby:</span> Fotografiert Architektur-Details (und erstellt Posts darüber)
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Fun Fact:</span> Aus einem Hotel-Lobby-Foto Content für 2 Wochen generieren
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Kern-Fähigkeiten */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Kern-Fähigkeiten</h4>
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                    <div className="flex items-start gap-1.5">
                                      <Lightbulb className="h-3 w-3 text-pink-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Photo-to-Content AI</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Target className="h-3 w-3 text-indigo-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Visual Analysis</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Zap className="h-3 w-3 text-cyan-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Behind-the-Scenes</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Calendar className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Multi-Format Visuals</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Signature Message */}
                              <div className="flex items-start gap-2 pt-1.5 border-t border-border/40">
                                <MessageSquare className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                                <p className="text-xs italic text-foreground leading-tight">
                                  <span className="font-semibold">EMMA's Signature:</span> "Ich bemitleide jeden, der ohne gute Visuals postet."
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>)
                  ) : (
                    /* Max Card with Flip Animation */
                    (<div className="space-y-2" data-testid="agent-wrapper-3">
                      <h3 className="text-center font-playfair font-bold text-lg text-foreground">
                        MAX - The Wordsmith
                      </h3>
                      <div 
                        className="flip-card-container group cursor-pointer"
                        style={{ perspective: '1000px' }}
                        data-testid="agent-card-3"
                      >
                        <div className="flip-card-inner relative w-full aspect-[3/4] transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                          {/* Front Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <img
                              src={agent.image}
                              alt={agent.alt}
                              className="w-full h-full object-cover"
                              data-testid="agent-img-3"
                            />
                          </div>
                          
                          {/* Back Side */}
                          <div className="flip-card-face absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg overflow-hidden bg-card border shadow-lg">
                            <div className="w-full h-full p-3 flex flex-col justify-between">
                              <div className="space-y-2.5">
                                {/* Persönlichkeit */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Persönlichkeit</h4>
                                  <div className="space-y-1">
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Coffee:</span> Doppelter Espresso, doppelte Wirkung
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Talent:</span> Verkauft Eis an Eskimos - mit 7 Worten
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Quirk:</span> Spricht manchmal in Headlines
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Hobby:</span> Sammelt historische Werbetexte
                                    </p>
                                    <p className="text-[10px] leading-tight text-foreground">
                                      <span className="font-semibold">Fun Fact:</span> Speisekarte getextet - Umsatz verdoppelt, ohne Gericht zu ändern
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Kern-Fähigkeiten */}
                                <div className="space-y-1">
                                  <h4 className="font-bold text-sm text-foreground">Kern-Fähigkeiten</h4>
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                    <div className="flex items-start gap-1.5">
                                      <Zap className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Platform-Specific Copy</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Target className="h-3 w-3 text-teal-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Interview Formats</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Lightbulb className="h-3 w-3 text-orange-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Storytelling</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <TrendingUp className="h-3 w-3 text-rose-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Product Announcements</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <Calendar className="h-3 w-3 text-violet-500 mt-0.5 flex-shrink-0" />
                                      <span className="text-[10px] leading-tight text-foreground">Case Studies</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Signature Message */}
                              <div className="flex items-start gap-2 pt-1.5 border-t border-border/40">
                                <MessageSquare className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                                <p className="text-xs italic text-foreground leading-tight">
                                  <span className="font-semibold">MAX's Signature:</span> "Ich bin kein Texter, ich bin ein Wort-Künstler."
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>)
                  )}
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
      {/* Amplify Section - Below Agent Cards */}
      <AmplifySection />
      {/* Story Section - Below Amplify Section */}
      <StorySection />
      {/* How It Works Section - Below Story Section */}
      <HowItWorksSection />
    </section>
  );
}
